/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Checkbox,
  Divider,
  Fade,
  FormControlLabel,
  OutlinedInput,
  OutlinedInputProps,
  Popper,
  styled,
} from '@material-ui/core';
import PaperOutlined from '@src/components/UI/PaperOutlined';
import useOnClickOutside from '@src/hooks/useOnClickOutside';
import { DateType, DateTypeRange } from '@src/utils/fnsDateUtils';
import {
  endOfDay,
  endOfToday,
  format,
  getDayOfYear,
  getYear,
  isAfter,
  isValid,
  parse,
  setDayOfYear,
  setYear,
  startOfToday,
} from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Calendar, { CalendarProps } from './Calendar';

export type TimePickerInputProps = {
  className?: string;
  InputProps?: Partial<OutlinedInputProps>;
  label?: React.ReactNode;
  footer?: React.ReactNode;
  disabled?: boolean;
} & CalendarProps<false>;

export const TimePickerInput: React.FC<TimePickerInputProps> = ({
  onChange,
  value,
  InputProps,
  className,
  label,
  disabled,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(popupRef, event => {
    if (event.target !== inputRef.current) setOpen(false);
  });

  useEffect(() => {
    popupRef.current
      ?.querySelector('li.react-datepicker__time-list-item--selected')
      ?.scrollIntoView({ block: 'center' });
  }, [value]);

  const timeStr = useMemo(() => {
    if (isValid(value)) return format(value as Date, 'HH:mm');
    return '';
  }, [value]);

  const endDayOfValue = useMemo(() => {
    if (isValid(value)) return endOfDay(value as Date);
    return endOfToday();
  }, [value]);

  return (
    <>
      <TimeInputStyled
        label={label}
        disabled={disabled}
        className={`${className} ${open && 'Mui-focused'}`}
        fullWidth={false}
        spellCheck={false}
        autoComplete="off"
        inputRef={inputRef}
        onMouseDown={() => setOpen(true)}
        onFocus={() => setOpen(true)}
        type="time"
        value={timeStr}
        onChange={ev => {
          try {
            const nextValue = parse(
              ev.target.value,
              'HH:mm',
              value || startOfToday()
            );
            onChange(nextValue);
          } catch {
            onChange(null);
          }
        }}
        {...InputProps}
      />
      <Popper
        open={!disabled && open}
        anchorEl={inputRef.current}
        transition
        placement="bottom"
        role="combobox"
        style={{ zIndex: 2000 }}
        ref={popupRef}
        keepMounted={false}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={250}>
            <PaperStyled>
              <Calendar
                {...({
                  selectsRange: false,
                  value,
                  onChange,
                  showTimeSelect: true,
                  showTimeSelectOnly: true,
                  timeFormat: 'hh:mm aaa',
                  timeIntervals: 30,
                  injectTimes: [endDayOfValue],
                  ...props,
                } as unknown as CalendarProps)}
              />
            </PaperStyled>
          </Fade>
        )}
      </Popper>
    </>
  );
};

const TimeInputStyled = styled(OutlinedInput)({
  fontSize: '0.98rem',
  width: 90,
  padding: 0,
  '& input': {
    padding: 8,
  },
  '& input[type="time"]::-webkit-calendar-picker-indicator': {
    display: 'none',
    '-webkit-appearance': 'none',
  },
});

const PaperStyled = styled(PaperOutlined)({
  margin: 4,
  overflow: 'hidden',
});
function setDatePart(target: DateType, source: DateType) {
  let result = target as Date;
  result = setYear(result, getYear(source as Date));
  result = setDayOfYear(result, getDayOfYear(source as Date));
  return result;
}

function isDateRangeEqual(a: DateTypeRange, b: DateTypeRange) {
  const [startA, endA] = a || [];
  const [startB, endB] = b || [];

  return (
    startA?.getTime() === startB?.getTime() &&
    endA?.getTime() === endB?.getTime()
  );
}

type TimeRangePickerProps = {
  value: DateTypeRange;
  onChange: (value: DateTypeRange) => void;
  variant: 'inline' | 'column';
  enforceEndAfterStart?: boolean;
};

export const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  value: dateRange,
  onChange,
  variant,
  enforceEndAfterStart,
}) => {
  const isStartValid = isValid(dateRange[0]);
  const isEndValid = isValid(dateRange[1]);
  const disabled = !isStartValid && !isEndValid;

  const [timeRange, setTimeRange] = useState<DateTypeRange>(dateRange);

  const isWholeDay = useMemo(() => {
    const [start, end] = timeRange || [];
    if (isValid(start) && isValid(end)) {
      return (
        (start as Date).getHours() === 0 &&
        (start as Date).getMinutes() === 0 &&
        (end as Date).getHours() === 23 &&
        (end as Date).getMinutes() === 59
      );
    }
    return false;
  }, timeRange);

  const updateValue = (range: DateTypeRange) => {
    let [dateStart, dateEnd] = range || [];
    const [timeStart, timeEnd] = timeRange || [];

    if (isStartValid && isValid(timeStart)) {
      dateStart = setDatePart(timeStart, dateStart);
    }

    if (isEndValid && isValid(timeEnd)) {
      dateEnd = setDatePart(timeEnd, dateEnd);
    }

    if (isAfter(dateStart as Date, dateEnd as Date))
      [dateStart, dateEnd] = [dateEnd, dateStart];

    const nextRange: DateTypeRange = [dateStart, dateEnd];

    if (!isDateRangeEqual(nextRange, dateRange)) onChange(nextRange);
  };

  useEffect(() => {
    if (!isStartValid && !isEndValid) {
      setTimeRange([null, null]);
    } else if (isStartValid || isEndValid) {
      updateValue(dateRange);
    }
  }, [...timeRange, ...dateRange]);

  const setStartTime = (date: DateType) => {
    const start = date;
    let end = timeRange[1];

    if (
      enforceEndAfterStart &&
      isValid(start) &&
      isValid(end) &&
      isAfter(start as Date, setDatePart(end, start))
    ) {
      end = setDatePart(start, end);
    }

    setTimeRange([start, end]);
  };

  const setEndTime = (date: DateType) => {
    let start = timeRange[0];
    const end = date;

    if (
      enforceEndAfterStart &&
      isValid(start) &&
      isValid(end) &&
      isAfter(start as Date, setDatePart(end, start))
    ) {
      start = setDatePart(end, start);
    }

    setTimeRange([start, end]);
  };

  return (
    <>
      <Divider />
      <Box pl={2} p={1} my={1}>
        <Box fontSize="1rem" fontWeight={500}>
          <FormattedMessage id="DateRangePicker.schedule" />
        </Box>
        <Box
          display="flex"
          flexDirection={variant === 'inline' ? 'row' : 'column'}
          fontSize="0.944rem"
        >
          <FormControlLabel
            label={
              <Box fontSize="0.944rem">
                <FormattedMessage id="DateRangePicker.wholeDay" />
              </Box>
            }
            control={
              <Checkbox
                checked={isWholeDay}
                onChange={(_, checked) => {
                  if (checked) setTimeRange([startOfToday(), endOfToday()]);
                }}
              />
            }
          />
          <Box display="flex" alignItems="center" gridGap={12}>
            <TimePickerInput
              value={timeRange[0]}
              onChange={setStartTime}
              disabled={disabled}
            />
            <span>
              <FormattedMessage id="DateRangePicker.timeSeparator" />
            </span>
            <TimePickerInput
              value={timeRange[1]}
              onChange={setEndTime}
              disabled={disabled}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};
